import React, { useState } from 'react';
import { useCart } from '../CartContext';
import { useAuth } from '../AuthContext';
import { db, collection, addDoc, serverTimestamp } from '../firebase';
import { DELIVERY_LOCATIONS } from '../data';
import toast from 'react-hot-toast';
import './CheckoutPage.css';

const PAYMENT_METHODS = [
  { id: 'cod',  label: 'Cash on Delivery', icon: '💵', desc: 'Pay when delivered' },
  { id: 'upi',  label: 'UPI / Google Pay',  icon: '📱', desc: 'Instant payment' },
  { id: 'card', label: 'Credit / Debit Card',icon: '💳', desc: 'Visa, Mastercard, RuPay' },
];

export default function CheckoutPage({ navigate }) {
  const { items, total, dispatch } = useCart();
  const { user } = useAuth();
  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [upiId, setUpiId]   = useState('');
  const [form, setForm]     = useState({
    name:     user?.name || '',
    phone:    '',
    email:    user?.email || '',
    address:  '',
    city:     'Pune',
    pincode:  '',
    landmark: '',
  });

  const delivery    = total > 500 ? 0 : 49;
  const tax         = Math.round(total * 0.05);
  const grandTotal  = total + delivery + tax;

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const validateStep1 = () => {
    if (!form.name || !form.phone || !form.address || !form.pincode) {
      toast.error('Please fill all required fields'); return false;
    }
    if (!/^\d{10}$/.test(form.phone)) { toast.error('Enter valid 10-digit phone'); return false; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error('Enter valid 6-digit pincode'); return false; }
    return true;
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    const deliveryLoc = DELIVERY_LOCATIONS[Math.floor(Math.random() * DELIVERY_LOCATIONS.length)];
    const orderId = 'FM' + Date.now();

    const orderData = {
      id:              orderId,
      userId:          user?.uid,
      customer:        form,
      items:           items.map(i => ({ id:i.id, name:i.name, image:i.image, price:i.price, unit:i.unit, qty:i.qty })),
      subtotal:        total,
      delivery,
      tax,
      total:           grandTotal,
      paymentMethod,
      paymentDetails:  paymentMethod === 'upi' ? { upiId } : {},
      status:          'confirmed',
      deliveryLocation: deliveryLoc,
      estimatedMinutes: Math.floor(Math.random() * 20) + 25,
      createdAt:       serverTimestamp(),
    };

    const paymentData = {
      orderId,
      userId:       user?.uid,
      customerName: form.name,
      customerEmail: form.email,
      amount:       grandTotal,
      method:       paymentMethod,
      status:       paymentMethod === 'cod' ? 'pending' : 'paid',
      currency:     'INR',
      createdAt:    serverTimestamp(),
    };

    try {
      // Save order to Firestore
      await addDoc(collection(db, 'orders'), orderData);
      // Save payment record to Firestore
      await addDoc(collection(db, 'payments'), paymentData);
      toast.success('🎉 Order placed successfully!');
    } catch (err) {
      console.error('Firestore error:', err);
      toast.error('Failed to save order. Check Firebase config.');
      setLoading(false);
      return;
    }

    dispatch({ type: 'CLEAR_CART' });
    setLoading(false);
    navigate('track', { ...orderData, createdAt: new Date().toISOString() });
  };

  if (!items.length) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', gap:16 }}>
      <span style={{ fontSize:64 }}>🛒</span>
      <h2>Your cart is empty</h2>
      <button className="btn-primary-co" onClick={() => navigate('products')}>Shop Now</button>
    </div>
  );

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <button className="co-back-btn" onClick={() => step > 1 ? setStep(s=>s-1) : navigate('cart')}>← Back</button>
        <div className="co-brand" onClick={() => navigate('home')}>🌿 FreshMart</div>
        <div className="co-secure">🔒 Secure Checkout</div>
      </div>

      <div className="checkout-container">
        {/* Steps */}
        <div className="steps-bar">
          {['Delivery','Payment','Confirm'].map((s,i) => (
            <React.Fragment key={s}>
              <div className={`step-item ${step > i+1 ? 'done' : ''} ${step === i+1 ? 'active' : ''}`}>
                <div className="step-circle">{step > i+1 ? '✓' : i+1}</div>
                <span>{s}</span>
              </div>
              {i < 2 && <div className={`step-connector ${step > i+1 ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="checkout-layout">
          <div className="checkout-main">

            {/* ── STEP 1: ADDRESS ─────────────────────────── */}
            {step === 1 && (
              <div className="co-card">
                <h2>📦 Delivery Details</h2>
                <div className="form-grid">
                  {[
                    { name:'name',     label:'Full Name *',          placeholder:'Arjun Sharma',       type:'text',  full:false },
                    { name:'phone',    label:'Phone Number *',        placeholder:'98765 43210',         type:'tel',   full:false },
                    { name:'email',    label:'Email Address',         placeholder:'arjun@example.com',   type:'email', full:true  },
                    { name:'address',  label:'Delivery Address *',    placeholder:'Flat, Building, Street…', type:'textarea', full:true },
                    { name:'landmark', label:'Landmark',              placeholder:'Near park, behind school…', type:'text', full:false },
                    { name:'city',     label:'City',                  placeholder:'Pune',                type:'text',  full:false },
                    { name:'pincode',  label:'Pincode *',             placeholder:'411001',              type:'text',  full:false },
                  ].map(f => (
                    <div key={f.name} className={`form-group ${f.full ? 'full' : ''}`}>
                      <label>{f.label}</label>
                      {f.type === 'textarea'
                        ? <textarea name={f.name} placeholder={f.placeholder} value={form[f.name]} onChange={handleChange} rows={3} />
                        : <input name={f.name} type={f.type} placeholder={f.placeholder} value={form[f.name]} onChange={handleChange} />
                      }
                    </div>
                  ))}
                </div>
                <button className="co-next-btn" onClick={() => validateStep1() && setStep(2)}>
                  Continue to Payment →
                </button>
              </div>
            )}

            {/* ── STEP 2: PAYMENT ─────────────────────────── */}
            {step === 2 && (
              <div className="co-card">
                <h2>💳 Payment Method</h2>
                <div className="payment-options">
                  {PAYMENT_METHODS.map(pm => (
                    <button key={pm.id} className={`payment-option ${paymentMethod===pm.id?'selected':''}`} onClick={() => setPaymentMethod(pm.id)}>
                      <span className="pm-icon">{pm.icon}</span>
                      <div className="pm-text"><p>{pm.label}</p><small>{pm.desc}</small></div>
                      <div className={`pm-radio ${paymentMethod===pm.id?'checked':''}`} />
                    </button>
                  ))}
                </div>

                {paymentMethod === 'upi' && (
                  <div className="extra-field">
                    <label>Your UPI ID</label>
                    <input placeholder="yourname@paytm  or  @gpay" value={upiId} onChange={e=>setUpiId(e.target.value)} />
                    <div className="upi-apps">
                      {['GPay','PhonePe','Paytm','BHIM'].map(a=>(
                        <span key={a} className="upi-chip">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
                {paymentMethod === 'card' && (
                  <div className="card-form-grid">
                    <div className="form-group full"><label>Card Number</label><input placeholder="1234  5678  9012  3456" maxLength={19} /></div>
                    <div className="form-group"><label>Expiry (MM/YY)</label><input placeholder="08/27" maxLength={5} /></div>
                    <div className="form-group"><label>CVV</label><input placeholder="•••" maxLength={3} type="password" /></div>
                    <div className="form-group full"><label>Name on Card</label><input placeholder={form.name} /></div>
                  </div>
                )}

                <div className="step-actions">
                  <button className="co-back-step" onClick={() => setStep(1)}>← Back</button>
                  <button className="co-next-btn" onClick={() => setStep(3)}>Review Order →</button>
                </div>
              </div>
            )}

            {/* ── STEP 3: CONFIRM ─────────────────────────── */}
            {step === 3 && (
              <div className="co-card">
                <h2>✅ Review & Place Order</h2>

                <div className="confirm-section">
                  <div className="confirm-section-header"><span>📦</span><h4>Delivery Address</h4></div>
                  <p><strong>{form.name}</strong> · 📞 {form.phone}</p>
                  <p>{form.address}{form.landmark?`, near ${form.landmark}`:''}</p>
                  <p>{form.city} — {form.pincode}</p>
                </div>

                <div className="confirm-section">
                  <div className="confirm-section-header"><span>💳</span><h4>Payment</h4></div>
                  <p>{PAYMENT_METHODS.find(p=>p.id===paymentMethod)?.icon} {PAYMENT_METHODS.find(p=>p.id===paymentMethod)?.label}</p>
                  {paymentMethod==='upi' && upiId && <p className="text-muted">UPI: {upiId}</p>}
                </div>

                <div className="confirm-section">
                  <div className="confirm-section-header"><span>🛒</span><h4>Items ({items.length})</h4></div>
                  <div className="confirm-items-list">
                    {items.map(item => (
                      <div key={item.id} className="confirm-item-row">
                        <img src={item.image} alt={item.name} onError={e=>e.target.style.display='none'} />
                        <span className="ci-name">{item.name}</span>
                        <span className="ci-qty">×{item.qty}</span>
                        <span className="ci-price">₹{(item.price*item.qty).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="step-actions">
                  <button className="co-back-step" onClick={() => setStep(2)}>← Back</button>
                  <button className="place-order-btn" onClick={handlePlaceOrder} disabled={loading}>
                    {loading
                      ? <><span className="spinner" /> Placing Order…</>
                      : <>🎉 Place Order · ₹{grandTotal.toLocaleString()}</>
                    }
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── ORDER SUMMARY SIDEBAR ───────────────────── */}
          <div className="co-summary">
            <div className="co-summary-card">
              <h3>Order Summary</h3>
              <div className="co-items-list">
                {items.map(item => (
                  <div key={item.id} className="co-item-row">
                    <div className="co-item-img">
                      <img src={item.image} alt={item.name} onError={e=>e.target.style.display='none'} />
                      <span className="co-item-qty">{item.qty}</span>
                    </div>
                    <span className="co-item-name">{item.name}</span>
                    <span className="co-item-price">₹{(item.price*item.qty).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="co-summary-rows">
                <div className="co-s-row"><span>Subtotal</span><span>₹{total.toLocaleString()}</span></div>
                <div className="co-s-row"><span>Delivery</span><span className={delivery===0?'free-green':''}>{delivery===0?'🎉 FREE':`₹${delivery}`}</span></div>
                <div className="co-s-row"><span>GST (5%)</span><span>₹{tax}</span></div>
                <div className="co-divider" />
                <div className="co-s-row co-total"><span>Total</span><span>₹{grandTotal.toLocaleString()}</span></div>
              </div>
              <div className="eta-box">🕐 Estimated delivery: <strong>{Math.floor(Math.random()*20)+25}–45 min</strong></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
